import { useEffect, useMemo, useState } from 'react'
import {Plus, Search, Pencil, Trash2, User as UserIcon, KeyRound} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { maskCPF, maskPhone } from '@/utils/masks'
import UsuarioModal from '@/components/usuarios/UsuarioModal'
import ResetSenhaModal from '@/components/usuarios/ResetSenhaModal'

const roleLabels: Record<string, string> = {
  ADMIN: 'Administrador',
  MODERADOR: 'Moderador',
  GESTOR: 'Gestor',
  COORDENADOR: 'Coordenador',
}

const PAGE_SIZE = 10

function Usuarios() {
  const { user } = useAppAuth()
  const [usuarios, setUsuarios] = useState<any[]>([])
  const [cidades, setCidades] = useState<any[]>([])
  const [secretarias, setSecretarias] = useState<any[]>([])
  const [subLocais, setSubLocais] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [busca, setBusca] = useState('')
  const [page, setPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editando, setEditando] = useState<any | null>(null)
  const [excluindo, setExcluindo] = useState<any | null>(null)
  const [resetandoSenha, setResetandoSenha] = useState<any | null>(null)
  const isAdminLogado = user?.role === 'ADMIN'

  const fetchData = async () => {
    setLoading(true)
    try {
      const filter: Record<string, any> = {}
      if (user?.role === 'MODERADOR' && user.cidadeId)
        filter.cidadeId = user.cidadeId

      const [uRes, cRes, sRes, slRes] = await Promise.all([
        (lumi.entities as any).user_profiles.list({ filter, sort: { createdAt: -1 }, limit: 200 }),
        (lumi.entities as any).cidades.list({ sort: { nome: 1 }, limit: 200 }),
        (lumi.entities as any).secretarias.list({ sort: { nome: 1 }, limit: 200 }),
        (lumi.entities as any).sublocais.list({ sort: { nome: 1 }, limit: 500 }),
      ])
      setUsuarios(uRes?.list || [])
      setCidades(cRes?.list || [])
      setSecretarias(sRes?.list || [])
      setSubLocais(slRes?.list || [])
    }
    catch {
      setUsuarios([])
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const cidadeNome = (id?: string) => cidades.find(c => c._id === id)?.nome || '-'
  const secretariaNome = (id?: string) => secretarias.find(s => s._id === id)?.nome || '-'

  const filtrados = useMemo(() => {
    if (!busca) return usuarios
    const termo = busca.toLowerCase()
    return usuarios.filter((u) => {
      const alvo = `${u.nomeCompleto} ${u.cpf} ${u.email || ''}`.toLowerCase()
      return alvo.includes(termo)
    })
  }, [usuarios, busca])

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE))
  const paginaAtual = Math.min(page, totalPaginas)
  const paginaDados = filtrados.slice((paginaAtual - 1) * PAGE_SIZE, paginaAtual * PAGE_SIZE)

  const handleExcluir = async () => {
    if (!excluindo) return
    try {
      await (lumi.entities as any).user_profiles.delete(excluindo._id)
      toast.success('Utilizador excluído com sucesso.')
      setExcluindo(null)
      fetchData()
    }
    catch {
      toast.error('Erro ao excluir utilizador.')
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative w-full sm:w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" strokeWidth={1.75} />
          <input
            value={busca}
            onChange={(e) => { setBusca(e.target.value); setPage(1) }}
            placeholder="Buscar por nome, CPF ou e-mail..."
            className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF] bg-white"
          />
        </div>

        <button
          onClick={() => { setEditando(null); setShowModal(true) }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#1E4FBF] hover:bg-[#1A44A6] text-white whitespace-nowrap"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Novo Utilizador
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E7E9EC] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#E7E9EC] bg-[#FAFBFC]">
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Utilizador</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">CPF</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Telefone</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Cidade</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Secretaria</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Nível</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Estado</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F2F4]">
              {loading
                ? (
                    <tr>
                      <td colSpan={8} className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">A carregar...</td>
                    </tr>
                  )
                : paginaDados.length === 0
                  ? (
                      <tr>
                        <td colSpan={8} className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">Nenhum utilizador encontrado.</td>
                      </tr>
                    )
                  : paginaDados.map(u => (
                      <tr key={u._id} className="hover:bg-[#F7F8FA] transition-colors">
                        <td className="px-5 py-3 text-[13.5px] text-[#12141A]">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-[#F1F2F4] flex items-center justify-center overflow-hidden shrink-0">
                              {u.fotoUrl ? <img src={u.fotoUrl} alt="" className="w-full h-full object-cover" /> : <UserIcon className="w-4 h-4 text-[#9CA3AF]" strokeWidth={1.5} />}
                            </div>
                            <span className="font-medium truncate max-w-[160px]">{u.nomeCompleto}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-[13px] text-[#4B5563]">{maskCPF(u.cpf)}</td>
                        <td className="px-5 py-3 text-[13px] text-[#4B5563]">{u.telefone ? maskPhone(u.telefone) : '-'}</td>
                        <td className="px-5 py-3 text-[13px] text-[#4B5563]">{cidadeNome(u.cidadeId)}</td>
                        <td className="px-5 py-3 text-[13px] text-[#4B5563]">{(u.role === 'GESTOR' || u.role === 'COORDENADOR') ? secretariaNome(u.secretariaId) : '-'}</td>
                        <td className="px-5 py-3 text-[13px] text-[#4B5563]">{roleLabels[u.role] || u.role}</td>
                        <td className="px-5 py-3">
                          <span className={`text-[11.5px] font-medium px-2.5 py-1 rounded-full ${u.status === 'ativo' ? 'bg-[#EAF7EE] text-[#1E8A45]' : 'bg-[#F1F2F4] text-[#4B5563]'}`}>
                            {u.status === 'ativo' ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          {isAdminLogado && (
                            <button onClick={() => setResetandoSenha(u)} title="Redefinir senha" className="p-1.5 text-[#6B7280] hover:text-[#1E4FBF] rounded-lg hover:bg-[#EEF3FE]">
                              <KeyRound className="w-4 h-4" strokeWidth={1.75} />
                            </button>
                          )}
                          <button onClick={() => { setEditando(u); setShowModal(true) }} className="p-1.5 text-[#6B7280] hover:text-[#1E4FBF] rounded-lg hover:bg-[#EEF3FE]">
                            <Pencil className="w-4 h-4" strokeWidth={1.75} />
                          </button>
                          {!u.isSystemAdmin && (
                            <button onClick={() => setExcluindo(u)} className="p-1.5 text-[#6B7280] hover:text-[#B91C1C] rounded-lg hover:bg-[#FEF2F2]">
                              <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
            </tbody>
          </table>
        </div>

        {!loading && filtrados.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-[#E7E9EC]">
            <p className="text-[12.5px] text-[#9CA3AF]">
              Página {paginaAtual} de {totalPaginas} • {filtrados.length} resultados
            </p>
            <div className="flex gap-2">
              <button disabled={paginaAtual <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded-lg text-[12.5px] font-medium border border-[#D1D5DB] disabled:opacity-40 hover:bg-[#F5F6F8]">
                Anterior
              </button>
              <button disabled={paginaAtual >= totalPaginas} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded-lg text-[12.5px] font-medium border border-[#D1D5DB] disabled:opacity-40 hover:bg-[#F5F6F8]">
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <UsuarioModal
          usuario={editando}
          cidades={cidades}
          secretarias={secretarias}
          subLocais={subLocais}
          onClose={() => setShowModal(false)}
          onSaved={fetchData}
        />
      )}

      {resetandoSenha && (
        <ResetSenhaModal
          usuario={resetandoSenha}
          onClose={() => setResetandoSenha(null)}
          onReset={fetchData}
        />
      )}

      {excluindo && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-[420px] p-6">
            <h3 className="text-[15px] font-semibold text-[#12141A] mb-2">Excluir utilizador</h3>
            <p className="text-[13.5px] text-[#6B7280] mb-5">
              Tem certeza que deseja excluir <span className="font-medium text-[#12141A]">{excluindo.nomeCompleto}</span>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setExcluindo(null)} className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium border border-[#D1D5DB] hover:bg-[#F5F6F8]">
                Cancelar
              </button>
              <button onClick={handleExcluir} className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#B91C1C] hover:bg-[#991B1B] text-white">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Usuarios
